// useValidation.ts
import { SetStateAction } from 'react'
import { ValidationResultInterface, ValidationRulesInterface } from '../types/interfaces'

export const useValidation = (
  setValidationResult: React.Dispatch<SetStateAction<ValidationResultInterface[]>>,
  validationResult: ValidationResultInterface[],
  validations: ValidationRulesInterface[],
) => {
  const checkAndAddValidation = (fieldName: string) => {
    if (!validationResult.find((el) => el.name === fieldName)) {
      setValidationResult((prev) => [
        ...prev,
        {
          name: fieldName,
          isDirty: false,
          isEmpty: true,
          notValidFormat: false,
          tooShort: false,
          tooLong: false,
          notValid: false,
          isEmptyError: false,
          errorMessage: '',
        },
      ])
    }
  }

  const modifyResult = (
    fieldName: string,
    field: keyof ValidationResultInterface,
    bool: boolean,
    message?: string,
  ) => {
    setValidationResult((prev) =>
      prev.map((item) => {
        if (item.name === fieldName) {
          const updated = { ...item, [field]: bool }
          if (message !== undefined) {
            updated.errorMessage = message
          }
          return updated
        }
        return item
      }),
    )
  }

  const validate = <T>(fieldName: string, value: T, fieldType?: string) => {
    const itemValidations = validations.find((el) => el.fieldName === fieldName)
    if (!itemValidations) return

    // Сбрасываем ошибки
    modifyResult(fieldName, 'notValidFormat', false)
    modifyResult(fieldName, 'tooShort', false)
    modifyResult(fieldName, 'tooLong', false)
    modifyResult(fieldName, 'isEmpty', false)
    modifyResult(fieldName, 'isEmptyError', false)

    const strValue = String(value)
    const trimmedValue = fieldName === 'email' ? strValue : strValue.trim()

    // 1. Сначала проверяем на пустоту (самое важное)
    if (!itemValidations.canBeEmpty) {
      const isEmpty =
        value === undefined ||
        value === null ||
        (typeof value === 'string' && trimmedValue === '') ||
        (fieldType === 'numberText' && trimmedValue === '0') ||
        (Array.isArray(value) && value.length === 0)

      if (isEmpty) {
        modifyResult(fieldName, 'isEmpty', true)
        modifyResult(fieldName, 'isEmptyError', true, itemValidations.isEmptyMessage)
        modifyResult(fieldName, 'notValid', true)
        return
      }
    }

    // 2. Проверка для numberText
    if (fieldType === 'numberText') {
      const startsWithZero = trimmedValue.length > 0 && trimmedValue[0] === '0'
      const isOnlyZero = trimmedValue === '0'
      const zeroAllowed = itemValidations.canBeEmpty && isOnlyZero

      if (startsWithZero && !zeroAllowed) {
        modifyResult(fieldName, 'notValidFormat', true, 'Число не может начинаться с нуля')
        modifyResult(fieldName, 'notValid', true)
        return
      }

      if (/[^0-9]/.test(trimmedValue)) {
        modifyResult(fieldName, 'notValidFormat', true, 'Только цифры разрешены')
        modifyResult(fieldName, 'notValid', true)
        return
      }
    }

    // 3. Проверка формата (regex)
    if (itemValidations.format && !itemValidations.format.test(trimmedValue)) {
      modifyResult(
        fieldName,
        'notValidFormat',
        true,
        itemValidations.formatMessage || 'Неверный формат',
      )
      modifyResult(fieldName, 'notValid', true)
      return
    }

    // 4. Длины (только если прошли предыдущие проверки)
    if (itemValidations.minLength && trimmedValue.length < itemValidations.minLength) {
      modifyResult(fieldName, 'tooShort', true, itemValidations.toShortMessage)
      modifyResult(fieldName, 'notValid', true)
      return
    }

    if (itemValidations.maxLength && trimmedValue.length > itemValidations.maxLength) {
      modifyResult(fieldName, 'tooLong', true, itemValidations.tooLongMessage)
      modifyResult(fieldName, 'notValid', true)
      return
    }

    // 5. Если все проверки прошли - валидно
    modifyResult(fieldName, 'notValid', false)
  }

  const setDirty = (fieldName: string) => {
    setValidationResult((prev) =>
      prev.map((item) =>
        item.name === fieldName && !item.isDirty ? { ...item, isDirty: true } : item,
      ),
    )
  }

  return { validate, checkAndAddValidation, setDirty, modifyResult }
}
